import { IPrescriptionRepository } from '../interfaces';
import LogUtil from '../../../core/utils/LogUtil';

/**
 * Prescription Repository Implementation
 * 처방전 관리 API 호출 구현체
 */
export class PrescriptionRepositoryImpl extends IPrescriptionRepository {
  constructor(apiClient) {
    super();
    this.apiClient = apiClient;
    LogUtil.log('🔧 [PrescriptionRepository] 초기화 완료');
  }

  /**
   * 처방전 업로드
   * POST /prescriptions/upload
   */
  async uploadPrescription(data) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 처방전 업로드 요청:', data);
      const response = await this.apiClient.post('/prescriptions/upload', data);
      LogUtil.log('📥 [PrescriptionRepository] 처방전 업로드 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 처방전 업로드 실패:', error);
      throw new Error(`처방전 업로드 실패: ${error.message}`);
    }
  }

  /**
   * 처방전 목록 조회
   * GET /prescriptions/
   */
  async getPrescriptions(params = {}) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 처방전 목록 조회 요청:', params);
      const response = await this.apiClient.get('/prescriptions/', {
        params,
      });
      LogUtil.log(
        '📥 [PrescriptionRepository] 처방전 목록 조회 응답:',
        response
      );
      return response;
    } catch (error) {
      LogUtil.error(
        '❌ [PrescriptionRepository] 처방전 목록 조회 실패:',
        error
      );
      throw new Error(`처방전 목록 조회 실패: ${error.message}`);
    }
  }

  /**
   * 처방전 상세 조회
   * GET /prescriptions/{prescription_id}
   */
  async getPrescriptionById(prescriptionId) {
    try {
      LogUtil.log(
        '📤 [PrescriptionRepository] 처방전 상세 조회 요청:',
        prescriptionId
      );
      const response = await this.apiClient.get(
        `/prescriptions/${prescriptionId}`
      );
      LogUtil.log(
        '📥 [PrescriptionRepository] 처방전 상세 조회 응답:',
        response
      );
      return response;
    } catch (error) {
      LogUtil.error(
        '❌ [PrescriptionRepository] 처방전 상세 조회 실패:',
        error
      );
      throw new Error(`처방전 상세 조회 실패: ${error.message}`);
    }
  }

  /**
   * 약국 선택 및 주문 생성
   * POST /prescriptions/{prescription_id}/select-pharmacy
   */
  async selectPharmacy(prescriptionId, data) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 약국 선택 요청:', {
        prescriptionId,
        data,
      });
      const response = await this.apiClient.post(
        `/prescriptions/${prescriptionId}/select-pharmacy`,
        data
      );
      LogUtil.log('📥 [PrescriptionRepository] 약국 선택 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 약국 선택 실패:', error);
      throw new Error(`약국 선택 실패: ${error.message}`);
    }
  }

  /**
   * 처방전 삭제
   * DELETE /prescriptions/{prescription_id}
   */
  async deletePrescription(prescriptionId) {
    try {
      LogUtil.log(
        '📤 [PrescriptionRepository] 처방전 삭제 요청:',
        prescriptionId
      );
      const response = await this.apiClient.delete(
        `/prescriptions/${prescriptionId}`
      );
      LogUtil.log('📥 [PrescriptionRepository] 처방전 삭제 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 처방전 삭제 실패:', error);
      throw new Error(`처방전 삭제 실패: ${error.message}`);
    }
  }

  /**
   * 처방전 이미지 분석 (OCR)
   * POST /prescriptions/analyze
   */
  async analyzePrescription(data) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 처방전 분석 요청:', data);
      const response = await this.apiClient.post(
        '/prescriptions/analyze',
        data
      );
      LogUtil.log('📥 [PrescriptionRepository] 처방전 분석 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 처방전 분석 실패:', error);
      throw new Error(`처방전 분석 실패: ${error.message}`);
    }
  }

  // ==================== 대시보드용 API (약국 인증 필요) ====================

  /**
   * [대시보드] 복약 요청 목록 조회
   * GET /prescription/list
   */
  async getOrderList(params = {}, token, requestConfig = {}) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 복약 요청 목록 조회 요청:', params);
      const response = await this.apiClient.get('/prescription/list', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        ...requestConfig,
      });
      LogUtil.log('📥 [PrescriptionRepository] 복약 요청 목록 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 복약 요청 목록 조회 실패:', error);
      throw new Error(`복약 요청 목록 조회 실패: ${error.message}`);
    }
  }

  /**
   * [대시보드] 복약 요청 상태 변경
   * PATCH /orders/{order_id}/status
   */
  async updateOrderStatus(orderId, data, token) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 복약 요청 상태 변경 요청:', {
        orderId,
        data,
      });
      const response = await this.apiClient.patch(
        `/orders/${orderId}/status`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      LogUtil.log('📥 [PrescriptionRepository] 복약 요청 상태 변경 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 복약 요청 상태 변경 실패:', error);
      throw new Error(`복약 요청 상태 변경 실패: ${error.message}`);
    }
  }

  /**
   * [대시보드] 복약 요청 상세 조회
   * GET /prescription/{order_id}
   */
  async getOrderDetail(orderId, token) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 복약 요청 상세 조회 요청:', orderId);
      const response = await this.apiClient.get(
        `/prescription/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      LogUtil.log('📥 [PrescriptionRepository] 복약 요청 상세 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 복약 요청 상세 조회 실패:', error);
      throw new Error(`복약 요청 상세 조회 실패: ${error.message}`);
    }
  }

  /**
   * [대시보드] 환자 목록 조회
   * GET /prescription/patients
   */
  async getPatients(params = {}, token) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 환자 목록 조회 요청:', params);
      const response = await this.apiClient.get('/prescription/patients', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      LogUtil.log('📥 [PrescriptionRepository] 환자 목록 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 환자 목록 조회 실패:', error);
      throw new Error(`환자 목록 조회 실패: ${error.message}`);
    }
  }

  /**
   * [대시보드] 환자 상세 조회
   * GET /prescription/patients/{userId}
   */
  async getPatientDetail(userId, token) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 환자 상세 조회 요청:', userId);
      const response = await this.apiClient.get(
        `/prescription/patients/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      LogUtil.log('📥 [PrescriptionRepository] 환자 상세 조회 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 환자 상세 조회 실패:', error);
      throw new Error(`환자 상세 조회 실패: ${error.message}`);
    }
  }

  /**
   * [환자 상세] 환자별 처방전 리스트 조회 (N개)
   * GET /prescriptions/?user_id={userId}&skip=0&limit=20
   */
  async getUserPrescriptions(userId, params = {}) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 환자별 처방전 리스트:', { userId, ...params });
      const response = await this.apiClient.get('/prescriptions/', {
        params: { user_id: userId, skip: 0, limit: 20, ...params },
      });
      LogUtil.log('📥 [PrescriptionRepository] 환자별 처방전 리스트 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 환자별 처방전 리스트 조회 실패:', error);
      throw new Error(`환자별 처방전 리스트 조회 실패: ${error.message}`);
    }
  }

  /**
   * [환자 상세] 마지막 처방전 조회
   * GET /prescriptions/last-prescription/{userId}
   * 응답: { medications_resolved: [{ edi_code, medication_name, ... }], hospital_name, ... }
   */
  async getLastPrescription(userId) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 마지막 처방전 조회:', userId);
      const response = await this.apiClient.get(
        `/prescriptions/last-prescription/${userId}`,
      );
      LogUtil.log('📥 [PrescriptionRepository] 마지막 처방전 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 마지막 처방전 조회 실패:', error);
      throw new Error(`마지막 처방전 조회 실패: ${error.message}`);
    }
  }

  /**
   * [환자 상세] 복약지도문 (다건)
   * POST /medications/detail/list  body: { edi_codes: string[] }
   * 응답: { medications: [{ item_name, entp_name, pic_codes, efcy_qesitm, atpn_qesitm, ... }] }
   */
  async getMedicationGuide(ediCodes) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] 복약지도문 조회:', ediCodes);
      const response = await this.apiClient.post('/medications/detail/list', {
        edi_codes: ediCodes,
      });
      LogUtil.log('📥 [PrescriptionRepository] 복약지도문 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] 복약지도문 조회 실패:', error);
      throw new Error(`복약지도문 조회 실패: ${error.message}`);
    }
  }

  /**
   * [대시보드] 처방전 QR 코드 생성
   * POST /prescriptions/generate-qr
   */
  async generateQr(data, token) {
    try {
      LogUtil.log('📤 [PrescriptionRepository] QR 코드 생성 요청:', data);
      const response = await this.apiClient.post(
        '/prescriptions/generate-qr',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      LogUtil.log('📥 [PrescriptionRepository] QR 코드 생성 응답:', response);
      return response;
    } catch (error) {
      LogUtil.error('❌ [PrescriptionRepository] QR 코드 생성 실패:', error);
      throw new Error(`QR 코드 생성 실패: ${error.message}`);
    }
  }

  // ─── Mock (백엔드 endpoint 준비되면 실제 호출로 swap) ───
  // 백엔드 요청 명세: docs/BACKEND_API_REQUIREMENTS.md 참조
  async getAiInsight(_orderId) {
    return {
      result_code: '200',
      resultCode: 200,
      data: {
        insight_text:
          '홍길동님은 3종 약물이 처방되었습니다. 장기 처방 약물이 포함되어 있어 복약 순응도 관리가 중요합니다. 다제 복용 환자로 약물 상호작용 확인이 필요합니다.',
      },
    };
  }

  async getSubstitutions(_orderId) {
    return {
      result_code: '200',
      resultCode: 200,
      data: {
        original_drug: '서울 에제티미브정 · (주) 서울제약',
        substitute_drug: '에제티마이브정 10mg · 한미약품',
        price_diff: 200,
        patient_consent_status: 'pending', // 'pending' | 'agreed' | 'declined'
      },
    };
  }
}
